import { useState } from 'react'
import api from '../api/client'
import Modal from './Modal'
import Boton from './Boton'

/*
 * Condición fiscal del cliente, editable desde la NV.
 *
 * Caso típico: el pedido de Magento entra con CUIT → LGR deduce factura A,
 * pero el comprador quería B (cargó el CUIT por costumbre). El vendedor lo
 * corrige acá sin salir de la NV. Guarda EN LA FICHA del cliente (mismo
 * endpoint y validaciones que el ABM): la letra la decide Presea a partir de
 * la condición que le mandamos, así que forzar la letra ajusta también lo que
 * viaja (Cliente::tipoRespParaPresea) — el aviso de abajo lo explica.
 */

const TIPOS_DOC = [
  { value: 80, label: 'CUIT' },
  { value: 85, label: 'CUIL' },
  { value: 96, label: 'DNI' },
  { value: 94, label: 'Pasaporte' },
  { value: 99, label: 'Sin identificar' },
]

const TIPOS_RESP = [
  { value: 1.0, label: 'Responsable Inscripto (RI)' },
  { value: 3.1, label: 'Monotributo' },
  { value: 4.0, label: 'Exento' },
  { value: 5.0, label: 'Consumidor Final' },
]

const st = {
  label: {
    display: 'block', fontSize: '0.78rem', fontWeight: 600, color: '#475569',
    textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3,
  },
  input: {
    width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1',
    borderRadius: 6, fontSize: '0.9rem', boxSizing: 'border-box', background: '#fff',
  },
  err: { display: 'block', fontSize: '0.78rem', color: '#dc2626', marginTop: 3 },
  row2: { display: 'flex', gap: '0.75rem' },
}

export default function ModalCondicionFiscal({ cliente, onSaved, onClose }) {
  const [form, setForm] = useState({
    tipo_doc: cliente.tipo_doc ?? '',
    cuit: cliente.cuit || '',
    // Normalizado con parseFloat: el cast decimal:1 devuelve "5.0" y el
    // <option> vale "5" — sin esto el select se ve vacío (bug #18 del ABM).
    tipo_resp: cliente.tipo_resp != null && cliente.tipo_resp !== '' ? String(parseFloat(cliente.tipo_resp)) : '',
    tipo_factura: cliente.tipo_factura || '',
  })
  const [errors, setErrors] = useState({})
  const [error, setError] = useState(null)
  const [procesando, setProcesando] = useState(false)
  const [afipStatus, setAfipStatus] = useState(null)
  const [afipMsg, setAfipMsg] = useState('')

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }))

  // Misma validación ARCA que el alta: con 11 dígitos el padrón dice quién es
  // y qué condición tiene — acá solo completa lo FISCAL (este modal no toca
  // nombre ni domicilio de la ficha).
  const validarAfip = async () => {
    const cuit = form.cuit.replace(/\D/g, '')
    if (cuit.length !== 11) { setAfipMsg('Ingresá un CUIT de 11 dígitos.'); setAfipStatus('error'); return }
    setAfipStatus('loading')
    setAfipMsg('')
    try {
      const { data } = await api.get(`/afip/contribuyente/${cuit}`)
      setForm(f => ({
        ...f,
        tipo_doc: '80',
        tipo_resp: data.tipo_resp != null ? String(parseFloat(data.tipo_resp)) : f.tipo_resp,
      }))
      setAfipStatus('ok')
      const cond = { RI: 'Resp. Inscripto', MONOTRIBUTO: 'Monotributo', EXENTO: 'Exento', CF: 'Cons. Final' }[data.condicion_iva]
      setAfipMsg(`Validado: ${data.nombre}${cond ? ` · ${cond}` : ''}`)
    } catch (err) {
      setAfipStatus('error')
      setAfipMsg(err.response?.data?.error || 'No se pudo validar en AFIP.')
    }
  }

  const esRi = parseFloat(form.tipo_resp) === 1
  const letraAuto = esRi ? 'A' : 'B'
  const letraFinal = ['A', 'B'].includes(form.tipo_factura) ? form.tipo_factura : letraAuto
  const avisoFactura = form.tipo_factura === 'B' && esRi
    ? 'Está marcado como Responsable Inscripto: al emitirle B, en Presea queda registrado como Consumidor Final.'
    : (form.tipo_factura === 'A' && !esRi
      ? 'Al emitirle A, en Presea queda registrado como Responsable Inscripto. Necesita CUIT.'
      : '')

  const guardar = async () => {
    setError(null)
    setErrors({})
    setProcesando(true)
    try {
      const { data } = await api.put(`/clientes/${cliente.id}`, {
        tipo_doc: form.tipo_doc === '' ? null : parseInt(form.tipo_doc),
        cuit: form.cuit || null,
        tipo_resp: form.tipo_resp === '' ? null : parseFloat(form.tipo_resp),
        tipo_factura: form.tipo_factura || null,
      })
      onSaved(data)
    } catch (e) {
      const errs = e.response?.data?.errors
      if (errs) setErrors(errs)
      else setError(e.response?.data?.message || 'No se pudo guardar.')
      setProcesando(false)
    }
  }

  return (
    <Modal title="Condición fiscal del cliente" onClose={onClose}>
      <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 0 }}>
        <b>{cliente.nombre}</b> — los cambios se guardan en la ficha del cliente
        y valen para esta NV y las próximas.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={st.row2}>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Tipo de documento</label>
            <select style={st.input} value={form.tipo_doc} onChange={set('tipo_doc')}>
              <option value="">— Seleccionar —</option>
              {TIPOS_DOC.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={st.label}>CUIT / Documento</label>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <input style={{ ...st.input, flex: 1 }} value={form.cuit} onChange={e => { set('cuit')(e); setAfipStatus(null); setAfipMsg('') }} onBlur={() => { if (form.cuit.replace(/\D/g, '').length === 11 && afipStatus === null) validarAfip() }} autoComplete="off" />
              {form.cuit.replace(/\D/g, '').length === 11 && (
                <Boton variante="secondary" sm type="button" onClick={validarAfip} disabled={afipStatus === 'loading'} title="Validar en AFIP">
                  {afipStatus === 'loading' ? '...' : 'AFIP'}
                </Boton>
              )}
            </div>
            {afipMsg && <span style={{ ...st.err, color: afipStatus === 'ok' ? '#166534' : '#dc2626' }}>{afipMsg}</span>}
            {errors.cuit && <span style={st.err}>{errors.cuit[0]}</span>}
          </div>
        </div>

        <div style={st.row2}>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Condición IVA</label>
            <select style={st.input} value={form.tipo_resp} onChange={set('tipo_resp')}>
              <option value="">— Seleccionar —</option>
              {TIPOS_RESP.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Factura que se le emite</label>
            <select style={st.input} value={form.tipo_factura} onChange={set('tipo_factura')}>
              <option value="">Automático (por condición IVA) — {letraAuto}</option>
              <option value="A">Siempre factura A</option>
              <option value="B">Siempre factura B</option>
            </select>
            {errors.tipo_factura && <span style={st.err}>{errors.tipo_factura[0]}</span>}
          </div>
        </div>

        {avisoFactura && (
          <div style={{ fontSize: '0.82rem', color: '#92400e', background: '#fef3c7', padding: '8px 10px', borderRadius: 6 }}>
            {avisoFactura}
          </div>
        )}

        <div style={{ fontSize: '0.85rem', color: '#334155' }}>
          Con esto se le emite <b>factura {letraFinal}</b>.
        </div>
      </div>

      {error && <div style={{ ...st.err, marginTop: 8 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
        <Boton variante="ghost" type="button" onClick={onClose}>Cancelar</Boton>
        <Boton variante="primary" type="button" disabled={procesando} onClick={guardar}>
          {procesando ? 'Guardando…' : 'Guardar'}
        </Boton>
      </div>
    </Modal>
  )
}
